import { useCallback, useEffect, useMemo } from 'react'

import { sortBy } from 'lodash'
import { parseUnits } from 'viem'

import { useAutoSwap } from '~/components/forms/swap/hooks/useAutoSwap'
import { PaymentSourceRow } from '~/components/forms/transfer/fields/PaymentSource/PaymentSourceRow'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useMultipleSmartAccountBalances } from '~/hooks/useBalances'
import { useTokenFeatures } from '~/hooks/useTokenFeatures'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { isSameToken } from '~/utils/tokens'

import type { SelectOption } from '~/components/component-library'

export type PaymentSourceProps = {
  sender: IAddress
  destinationToken: {
    token: IAddress
    chainId: SupportedChainId
    amount: string
  }
  selectedToken?: {
    address: IAddress
    chainId: SupportedChainId
    amount: string
  }
  onSelect: (
    token: { address: IAddress; chainId: SupportedChainId } | undefined,
  ) => void
  isPrivate?: boolean
  error?: string
  // When true, only same-chain sources are offered (no bridging). The multisend
  // flow sets this; single-send leaves it off to allow cross-chain sources.
  preventBridge?: boolean
  // Offramp sends pay out the bank's settlement token, so the direct-pay row is
  // that token too. Unlike a normal direct send, its "amount available" isn't
  // redundant — it's how much of the settlement token you can offramp — so keep
  // it shown here.
  isOfframp?: boolean
  // Auto-vs-explicit marker, owned by the parent so it survives this field
  // unmounting (see PaymentSourceField).
  wasAutoSelectedRef: { current: boolean }
}

// Why a held balance can't be used. Topping up fixes a shortfall but never
// incompatibility, so the two are ordered — and grouped — by that difference.
const INSUFFICIENT_REASON = 'Insufficient balance'
const INCOMPATIBLE_REASON = 'Not compatible with private transfers'

// Encode/decode the token ↔ string SelectInput needs for its string-keyed value.
// chainId is always numeric, so the last `-` is the separator even if an address
// ever contains one (IAddress is a plain string for alt-L1 support).
const encodeToken = (address: IAddress, chainId: SupportedChainId) =>
  `${address}-${chainId}`
const decodeToken = (value: string) => {
  const idx = value.lastIndexOf('-')
  return {
    address: value.slice(0, idx) as IAddress,
    chainId: Number(value.slice(idx + 1)) as SupportedChainId,
  }
}

/**
 * Derives the "Payment source" dropdown's options, current selection, and
 * select handler from the sender's balances + the destination token. Kept out
 * of the component so the component is a thin SelectInput wrapper and this
 * logic is testable on its own.
 */
export const usePaymentSourceOptions = ({
  sender,
  destinationToken,
  selectedToken,
  onSelect,
  isPrivate,
  error,
  preventBridge,
  isOfframp,
  wasAutoSelectedRef,
}: PaymentSourceProps): {
  options: SelectOption<string>[]
  selectedValue: string | undefined
  selectValue: (value: string) => void
  // No option can be picked: either the list is empty, or everything on it is
  // disabled. The caller should surface an inline explanation.
  hasNoSelectableSource: boolean
} => {
  const { swapTokens: allSwapTokens, insufficientTokens } = useAutoSwap({
    sender,
    destinationToken,
    preventBridge,
  })
  const { hasFeature } = useTokenFeatures()

  // Tokens on networks the user has turned off can't be used as a payment
  // source at all — an org-level setting, not something the user can work
  // around, so these are dropped entirely rather than shown disabled.
  const { org } = useActiveOrg()
  const enabledChainIds = org?.smartAccountsChainIds
  const isChainEligible = useCallback(
    (token: { chainId: SupportedChainId }) =>
      enabledChainIds?.includes(token.chainId) ?? false,
    [enabledChainIds],
  )
  // Whether a private transfer definitely can't use this token, per the
  // provider's feature flags. `hasFeature` returns `undefined` while
  // `/api/tokens/features` is still loading — treated as "not yet known"
  // here (not disabled), so tokens don't flash "not compatible" on load.
  const isPrivateIneligible = useCallback(
    (token: { address: IAddress; chainId: SupportedChainId }) =>
      !!isPrivate &&
      hasFeature(
        token.chainId,
        Address.format(token.address),
        'privateTransfers',
      ) === false,
    [isPrivate, hasFeature],
  )
  // Feeds auto-selection, `best`, and viability — only fully usable sources
  // are silently chosen. `=== true` keeps still-loading feature support
  // ineligible, so nothing auto-selects before support is known.
  const isEligibleSource = useCallback(
    (token: { address: IAddress; chainId: SupportedChainId }) =>
      isChainEligible(token) &&
      (!isPrivate ||
        hasFeature(
          token.chainId,
          Address.format(token.address),
          'privateTransfers',
        ) === true),
    [isChainEligible, isPrivate, hasFeature],
  )
  const autoSwapTokens = useMemo(
    () => allSwapTokens.filter(isEligibleSource),
    [allSwapTokens, isEligibleSource],
  )
  // Chain-eligible swap sources, private-ineligible ones included — these
  // back the rendered options list, where private-incompatible tokens still
  // show up as disabled rows instead of disappearing. `autoSwapTokens` above
  // (full eligibility) is what auto-selection and viability read.
  const chainEligibleSwapTokens = useMemo(
    () => allSwapTokens.filter(isChainEligible),
    [allSwapTokens, isChainEligible],
  )

  // `useAutoSwap` excludes the destination token, so read the sender's balance
  // of it directly. Same query key as `useAutoSwap` uses, so React Query dedupes.
  const { balances: balancesMap } = useMultipleSmartAccountBalances([sender])
  const destinationBalance =
    (balancesMap[sender]?.balances ?? []).find((balance) =>
      isSameToken(balance, destinationToken),
    )?.amount ?? 0n

  const destinationTokenMetadata = useTokenMetadata({
    address: destinationToken.token,
    chainId: destinationToken.chainId,
    includePrice: true,
  })
  const destinationValue = encodeToken(
    destinationToken.token,
    destinationToken.chainId,
  )

  // The amount of the destination token this send needs, in its own units.
  // Reused for the "pay directly?" check and the direct-pay row's spend.
  const requiredAmount = parseUnits(
    destinationToken.amount || '0',
    destinationTokenMetadata?.decimals ?? 18,
  )

  // Pay directly only on an enabled network with enough of the destination
  // token. Sufficiency uses the same (spam-excluded) balance the swap options
  // read — not the spam-included `isInsufficientFunds`, which can resolve a beat
  // later. A zero balance never qualifies, even before an amount is entered.
  const canPayDirectly =
    (enabledChainIds?.includes(destinationToken.chainId) ?? false) &&
    destinationBalance > 0n &&
    destinationBalance >= requiredAmount

  // With no send amount there's nothing to quote.
  const hasSendAmount = parseFloat(destinationToken.amount || '0') > 0
  // A chosen swap source whose quote hasn't resolved yet. True immediately on
  // select (no debounce dep) so the spend shows loading at once; a failed quote
  // isn't pending — the field shows an inline error instead.
  const isQuotePending =
    hasSendAmount &&
    !!selectedToken &&
    parseFloat(selectedToken.amount || '0') <= 0 &&
    !error

  // The best available swap source — useAutoSwap returns them pre-ranked (see
  // the `orderBy` keys there). Undefined while the list is still empty
  // (loading). Reused for the default selection below.
  const best = autoSwapTokens[0]

  // Whether the current selection is still one of the offered sources. Private
  // transfers can source from the destination token itself (a same-token
  // private transfer), which useAutoSwap excludes — it stays viable as long as
  // its balance covers the send.
  const selectedIsViable =
    !!selectedToken &&
    (autoSwapTokens.some((t) => isSameToken(t, selectedToken)) ||
      (!!isPrivate &&
        canPayDirectly &&
        isSameToken(selectedToken, destinationToken)))

  // Sync the chosen swap source with the (async) options list. Runs on render
  // because balances/quotes arrive after paint — there's no user event to commit
  // these from. Each branch is explained inline.
  // eslint-disable-next-line local/noUseEffect -- reacts to async balances; commits the sellToken the quote/submit read
  useEffect(() => {
    // Private transfers route through a provider even when paying with the
    // destination token, so commit it as the sell source — the quote and
    // submit read swapData.sellToken; the display-only default selection
    // below isn't enough. Doesn't wait on `best`: no swap list is needed.
    if (isPrivate && !selectedToken && canPayDirectly) {
      wasAutoSelectedRef.current = true
      onSelect({
        address: destinationToken.token,
        chainId: destinationToken.chainId,
      })
      return
    }

    // An empty list is transient (loading / refetch) — never act on it.
    if (!best) return

    // The selected source fell out of the list (its balance no longer covers the
    // send) — clear it rather than leave a stale, unselectable source.
    if (selectedToken && !selectedIsViable) {
      wasAutoSelectedRef.current = false
      onSelect(undefined)
      return
    }

    // Revert an auto-selected source once a direct send is possible again (e.g.
    // the user corrected an overshot amount) so they aren't silently left paying
    // swap fees. An explicit choice clears the ref in `selectValue`, so it
    // sticks. Never for private transfers — clearing the source would drop the
    // send back to a direct transfer.
    if (
      selectedToken &&
      canPayDirectly &&
      wasAutoSelectedRef.current &&
      !isPrivate
    ) {
      wasAutoSelectedRef.current = false
      onSelect(undefined)
      return
    }

    // Nothing chosen and the balance is short — auto-select the best source.
    if (!selectedToken && !canPayDirectly) {
      wasAutoSelectedRef.current = true
      onSelect({ address: best.address, chainId: best.chainId })
    }
  }, [
    selectedToken,
    selectedIsViable,
    canPayDirectly,
    best,
    isPrivate,
    destinationToken.token,
    destinationToken.chainId,
    onSelect,
    wasAutoSelectedRef,
  ])

  const destinationOption: SelectOption<string> = {
    value: destinationValue,
    disabled: !canPayDirectly,
    disabledReason: canPayDirectly ? undefined : INSUFFICIENT_REASON,
    label: (
      <PaymentSourceRow
        address={destinationToken.token}
        chainId={destinationToken.chainId}
        available={destinationBalance}
        spend={requiredAmount}
        // Source token === token the recipient gets, so "amount available" is
        // redundant — you're paying directly. The exception is an offramp, where
        // the available settlement-token balance is meaningful (see isOfframp).
        hideAvailable={!isOfframp && canPayDirectly}
      />
    ),
  }

  const swapOptions: SelectOption<string>[] = chainEligibleSwapTokens.map(
    ({ address, chainId, amount, sellEstimate, metadata }) => {
      const isSelected =
        !!selectedToken && isSameToken({ address, chainId }, selectedToken)
      // For the selected source, show the real quoted sell amount once it
      // resolves; until then fall back to the estimate and let TokenBalance
      // show its own loading state on the spend.
      const spend =
        isSelected && parseFloat(selectedToken.amount || '0') > 0
          ? parseUnits(selectedToken.amount, metadata.decimals ?? 18)
          : sellEstimate
      // Held and enough to cover the send, but not usable for a private
      // transfer — show it, disabled, instead of dropping it entirely.
      const disabled = isPrivateIneligible({ address, chainId })
      return {
        value: encodeToken(address, chainId),
        disabled,
        disabledReason: disabled ? INCOMPATIBLE_REASON : undefined,
        label: (
          <PaymentSourceRow
            address={address}
            chainId={chainId}
            available={amount}
            spend={spend}
            isLoadingSpend={isSelected && isQuotePending}
          />
        ),
      }
    },
  )

  const insufficientOptions: SelectOption<string>[] = insufficientTokens
    .filter(isChainEligible)
    .map(({ address, chainId, amount, sellEstimate }) => ({
      value: encodeToken(address, chainId),
      disabled: true,
      // When a token is both short and unusable for a private transfer, name the
      // one topping up can't fix — otherwise we'd send the user off to buy
      // more of a token that still wouldn't work.
      disabledReason: isPrivateIneligible({ address, chainId })
        ? INCOMPATIBLE_REASON
        : INSUFFICIENT_REASON,
      label: (
        <PaymentSourceRow
          address={address}
          chainId={chainId}
          available={amount}
          spend={sellEstimate}
        />
      ),
    }))

  const heldDestinationOption =
    destinationBalance > 0n && isChainEligible(destinationToken)
      ? [destinationOption]
      : []

  // Keep every held, eligible balance visible, ordered by what the user can do
  // about it: sources that work, then ones a top-up would fix, then ones no
  // amount of balance would make usable. The sort is stable, so the destination
  // leads its group (paying directly needs no swap).
  const options = sortBy(
    [...heldDestinationOption, ...swapOptions, ...insufficientOptions],
    (option) =>
      !option.disabled
        ? 0
        : option.disabledReason === INSUFFICIENT_REASON
          ? 1
          : 2,
  )

  // Pre-select a payment source: the destination when you can pay directly,
  // otherwise the best available swap source (the effect mirrors this into form
  // state). Unselected only when neither is possible.
  const defaultSelection = canPayDirectly
    ? destinationValue
    : best
      ? encodeToken(best.address, best.chainId)
      : undefined
  const resolvedValue = selectedToken
    ? encodeToken(selectedToken.address, selectedToken.chainId)
    : defaultSelection
  // The sync effect can't clear a stale selection while the swap list is empty
  // (its `!best` guard returns first), so a now-disabled selection can linger
  // in form state — never render a disabled option as selected.
  const selectedValue = options.find((option) => option.value === resolvedValue)
    ?.disabled
    ? undefined
    : resolvedValue

  const selectValue = (value: string) => {
    // SelectInput blocks disabled options too, but this hook's API can't
    // rely on its caller for that.
    if (options.find((option) => option.value === value)?.disabled) return

    // An explicit choice (direct or swap) is sticky — clear the auto-selected
    // marker so the effect won't later revert it.
    wasAutoSelectedRef.current = false
    const decoded = decodeToken(value)
    onSelect(
      isSameToken(decoded, destinationToken) && !isPrivate
        ? undefined
        : decoded,
    )
  }

  return {
    options,
    selectedValue,
    selectValue,
    hasNoSelectableSource: options.every((option) => option.disabled),
  }
}
